import { useMemo } from 'react';

import {
  COMPOSITION_GET_DATA_V3,
  COMPOSITION_GET_DATA_WITH_ACTIVITIES_V3,
  type CompositionV3Req,
  type EntKind,
} from '@revelio/data-access';

import { activityBreakdownLevelFromFilters } from '../../../filters/utils/composition-v3/getCompositionV3BreakdownOptions';
import {
  getBreakdownLevelAtDepth,
  getBreakdownLevelDepth,
} from '../../../filters/utils/composition-v3/treemapNestedLevels';
import type { TreemapDrilldownSegment } from '../../../filters/utils/treemapDrilldownLevels';
import {
  useOptionalRevelioPageId,
  useOptionalTabContentItems,
} from '../../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../PageConfigProvider/types';
import {
  ICICLE_BREAKDOWN_TOP_N,
  planTaxonomyIcicleRequests,
} from './taxonomy-icicle/plan-taxonomy-icicle-requests';
import { ICICLE_TAXONOMIES } from './taxonomy-icicle/taxonomies/taxonomy-registry';

export type OverviewSharedSegment = 'skills' | 'role' | 'activities';

/** The groups a page draws, or the cells a level beneath them. */
export type OverviewSharedTier = 'groups' | 'cells';

export type OverviewSharedRequest = {
  /** The taxonomy the page is about, which its icicle breaks down. */
  ownSegment: OverviewSharedSegment;
  withActivities: boolean;
  query:
    | typeof COMPOSITION_GET_DATA_V3
    | typeof COMPOSITION_GET_DATA_WITH_ACTIVITIES_V3;
};

const OWN_SEGMENT_BY_PAGE: Partial<
  Record<RevelioPageId, OverviewSharedSegment>
> = {
  [RevelioPageId.SKILL_COMPOSITION]: 'skills',
  [RevelioPageId.ROLE_COMPOSITION]: 'role',
  [RevelioPageId.ACTIVITY_COMPOSITION]: 'activities',
};

/**
 * An Overview page's charts scan one population, so they share two requests:
 * one for the groups, one for the cells a level down on every dimension.
 * Null on any other page.
 */
export const useOverviewSharedRequest = (): OverviewSharedRequest | null => {
  const revelioPageId = useOptionalRevelioPageId();
  const items = useOptionalTabContentItems();
  const ownSegment =
    revelioPageId === undefined || revelioPageId === null
      ? undefined
      : OWN_SEGMENT_BY_PAGE[revelioPageId];
  const breaksDownActivities = (items ?? []).some(
    (item) => item.plot?.secondaryDimension === 'activities'
  );
  return useMemo(() => {
    if (ownSegment === undefined) return null;
    const withActivities = ownSegment === 'activities' || breaksDownActivities;
    if (ownSegment !== 'role' && !withActivities) return null;
    return {
      ownSegment,
      withActivities,
      query: withActivities
        ? COMPOSITION_GET_DATA_WITH_ACTIVITIES_V3
        : COMPOSITION_GET_DATA_V3,
    };
  }, [ownSegment, breaksDownActivities]);
};

export const isOverviewSharedSegment = (
  shared: OverviewSharedRequest,
  segment: TreemapDrilldownSegment
): segment is OverviewSharedSegment =>
  segment === 'skills' ||
  segment === 'role' ||
  (segment === 'activities' && shared.withActivities);

const oneLevelDown = (
  segment: TreemapDrilldownSegment,
  level: EntKind
): EntKind => {
  const depth = getBreakdownLevelDepth(segment, level, []);
  return depth === null
    ? level
    : (getBreakdownLevelAtDepth(segment, depth + 1, []) ?? level);
};

const NO_TAXONOMY_CONTEXT = {
  activeFilterMap: new Map(),
  geographySelectionListIds: [],
};

/**
 * The levels the page's icicle asks for under the requested entities, for the
 * taxonomies whose ladder is known without their tree. Skills have no such
 * ladder: their levels follow the filters, like any other dimension's.
 */
const ownIcicleLevels = (
  ownSegment: OverviewSharedSegment,
  entities: CompositionV3Req['entities']
): Partial<Record<OverviewSharedTier, EntKind>> => {
  if (ownSegment === 'skills') return {};
  const taxonomy = ICICLE_TAXONOMIES[ownSegment];
  const levelIds = taxonomy.levelIds([], NO_TAXONOMY_CONTEXT);
  if (levelIds === null) return {};
  const levels = levelIds.map((levelId) => taxonomy.entKind(levelId));
  const chips = (entities ?? []).flatMap((entity) => {
    const rootDepth = levels.indexOf(entity.EntType);
    return rootDepth === -1
      ? []
      : [{ key: `${entity.EntType}:${entity.EntID}`, name: '', rootDepth }];
  });
  if (chips.length === 0) return {};
  const [first, second] = planTaxonomyIcicleRequests({
    overlapping: taxonomy.overlapping,
    tiersBelowRoot: taxonomy.tiersBelowRoot,
    levelIds,
    entities: chips,
    drill: null,
  }).map(({ depth }) => levels[depth] ?? undefined);
  return taxonomy.overlapping
    ? { groups: first, cells: second ?? first }
    : { cells: first };
};

const LEVEL_FIELD = {
  skills: 'skill_breakdown_level',
  role: 'role_breakdown_level',
  activities: 'activity_breakdown_level',
} as const;

const TOP_N_FIELD = {
  skills: 'skill_top_n',
  role: 'role_top_n',
  activities: 'activities_top_n',
} as const;

const SEGMENTS: readonly OverviewSharedSegment[] = [
  'skills',
  'role',
  'activities',
];

export const withOverviewSharedBreakdowns = (
  req: CompositionV3Req,
  shared: OverviewSharedRequest,
  {
    tier = 'groups',
    owns,
  }: {
    tier?: OverviewSharedTier;
    /** The dimension the caller set the level of itself, for this tier. */
    owns?: TreemapDrilldownSegment;
  } = {}
): CompositionV3Req => {
  const options = req.breakdown_options;
  const icicleLevels = ownIcicleLevels(shared.ownSegment, req.entities);
  const overlappingIcicle = ICICLE_TAXONOMIES[shared.ownSegment].overlapping;

  const requestedLevel = (segment: OverviewSharedSegment): EntKind =>
    segment === 'activities'
      ? (options.activity_breakdown_level ??
        activityBreakdownLevelFromFilters(req.filters))
      : options[LEVEL_FIELD[segment]];

  const levelOf = (segment: OverviewSharedSegment): EntKind => {
    const requested = requestedLevel(segment);
    if (segment === owns) return requested;
    const icicleLevel =
      segment === shared.ownSegment ? icicleLevels[tier] : undefined;
    if (icicleLevel !== undefined) return icicleLevel;
    return tier === 'cells' ? oneLevelDown(segment, requested) : requested;
  };

  const limits = (segment: OverviewSharedSegment): boolean =>
    tier === 'cells' || (segment === shared.ownSegment && overlappingIcicle);

  const breakdowns = SEGMENTS.filter(
    (segment) => segment !== 'activities' || shared.withActivities
  ).flatMap((segment) => [
    [LEVEL_FIELD[segment], levelOf(segment)],
    ...(limits(segment)
      ? [
          [
            TOP_N_FIELD[segment],
            options[TOP_N_FIELD[segment]] ?? ICICLE_BREAKDOWN_TOP_N,
          ],
        ]
      : []),
  ]);

  return {
    ...req,
    breakdown_options: { ...options, ...Object.fromEntries(breakdowns) },
  };
};
